import {
  Flex,
  Skeleton,
  Typography,
  Popover,
  DatePicker,
  Grid,
  Modal,
} from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useState, useEffect, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { getActivity } from 'shared/config/store/selectors/app';
import { RootState } from 'shared/config/store/store';
import { getEMIMonthSpend, getEMIAccounts } from 'shared/config/store/reducers/accountsSlice';
import { useLazyGetMonthSpendQuery } from '../api';
import { EMI } from 'shared/model/types';
import classes from '../ui/Transactions.module.scss';

const { useBreakpoint } = Grid;

export const MonthSpendingAllAccounts = ({
  emi,
  start,
  onDateRangeChange 
}: { 
  emi: EMI,
  start: string,
  onDateRangeChange?: (startDate: string, endDate: string | undefined) => void
}) => {
  const { formatMessage: t } = useIntl();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [tempStart, setTempStart] = useState<Dayjs | null>(null);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const startDate = dateRange?.[0] ? dateRange[0].format('DD-MM-YYYY') : start;
  const endDate = dateRange?.[1] ? dateRange[1].format('DD-MM-YYYY') : undefined;

  const accounts = useSelector((state: RootState) => getEMIAccounts(state, emi));
  const accountIds = useMemo(() => Object.keys(accounts).map(id => Number(id)), [accounts]);

  const [fetchMonthSpend] = useLazyGetMonthSpendQuery();

  useEffect(() => {
    if (accountIds.length > 0) {
      accountIds.forEach(accountId => {
        fetchMonthSpend({ accountId, start: startDate, end: endDate });
      });
    }
  }, [startDate, endDate, accountIds, fetchMonthSpend]);

  const monthSpend = useSelector((state: RootState) => getEMIMonthSpend(state, emi));
  const activity = useSelector(getActivity);

  const totalMonthSpend = useMemo(() => {
    return Object.values(monthSpend || {}).reduce((sum, spend) => sum + (spend || 0), 0);
  }, [monthSpend]);

  const handleDesktopRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) return;

    setDateRange(dates);
    onDateRangeChange?.(
      dates[0].format('DD-MM-YYYY'),
      dates[1].format('DD-MM-YYYY')
    );
    setPopoverOpen(false);
  };

  const handleMobileDateSelect = (date: Dayjs | null) => {
    if (!date) return;

    if (!tempStart) {
      setTempStart(date);
      return;
    }

    const range: [Dayjs, Dayjs] =
      date.isBefore(tempStart)
        ? [date, tempStart]
        : [tempStart, date];

    setDateRange(range);
    onDateRangeChange?.(
      range[0].format('DD-MM-YYYY'),
      range[1].format('DD-MM-YYYY')
    );

    setTempStart(null);
    setModalOpen(false);
  };

  const getDisplayText = () => {
    if (dateRange?.[0] && dateRange?.[1]) {
      return t(
        { id: 'transactions.spent.period' },
        {
          start: dateRange[0].format('DD.MM.YYYY'),
          end: dateRange[1].format('DD.MM.YYYY')
        }
      );
    }
    return t(
      { id: 'transactions.spent' },
      { month: t({ id: `calendar.month.${new Date().getMonth()}` }) }
    );
  };

  const desktopPicker = (
    <DatePicker.RangePicker
      value={dateRange}
      onChange={handleDesktopRangeChange}
      format="DD.MM.YYYY"
      disabledDate={(current) => current && current.isAfter(dayjs())}
      getPopupContainer={(trigger) => trigger.parentElement!}
    />
  );

  const mobilePicker = (
    <>
      <Typography.Text style={{ marginBottom: 8, display: 'block' }}>
        {tempStart
          ? t({ id: 'transactions.selectEndDate' })
          : t({ id: 'transactions.selectStartDate' })}
      </Typography.Text>

      <DatePicker
        value={null}
        onChange={handleMobileDateSelect}
        format="DD.MM.YYYY"
        style={{ width: '100%' }}
        disabledDate={(current) =>
          !!current &&
          (current.isAfter(dayjs()) ||
            (!!tempStart && current.isBefore(tempStart)))
        }
      />
    </>
  );

  return (
    <>
      <Flex justify="space-between" align="center">
        <Flex align="center" gap={8}>
          {!isMobile && (
            <Popover
              content={desktopPicker}
              trigger="click"
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
            >
              <CalendarOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Popover>
          )}

          {isMobile && (
            <CalendarOutlined
              style={{ fontSize: 18, cursor: 'pointer' }}
              onClick={() => setModalOpen(true)}
            />
          )}

          <Typography.Text className={classes.text}>
            {getDisplayText()}
          </Typography.Text>
        </Flex>

        <Skeleton active loading={activity} paragraph={{ rows: 1 }}>
          <Typography.Text className={classes.balance}>
            {totalMonthSpend.toFixed(2)} €
          </Typography.Text>
        </Skeleton>
      </Flex>

      {isMobile && (
        <Modal
          open={modalOpen}
          onCancel={() => {
            setModalOpen(false);
            setTempStart(null);
          }}
          footer={null}
          getContainer={false}
          mask={false}
          style={{
            top: '70%'
          }}
        >
          {mobilePicker}
        </Modal>
      )}
    </>
  );
};